import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaSync,
  FaDownload,
  FaUserMd,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaCalendar,
  FaArrowLeft,
} from "react-icons/fa";
import { buildApiUrl, API_ENDPOINTS } from "../../utils/apiConfig";
import { getTodayInIST, formatAppointmentDate } from "../../utils/dateUtils";
import PatientProfile from "../../components/PatientProfile";
import "./Admin.css";

/** Preferred date field: CSV uses "Preferred Date", PostgreSQL uses preferred_date */
function getPreferredDateRaw(apt) {
  return (
    apt["Preferred Date"] ??
    apt.preferred_date ??
    apt.preferredDate ??
    null
  );
}

/** Normalize to YYYY-MM-DD to match <input type="date" /> (handles ISO strings safely) */
function normalizePreferredDateToYmd(value) {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    if (trimmed.includes("T")) {
      const part = trimmed.split("T")[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(part)) return part;
    }
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
    d.getUTCDate()
  ).padStart(2, "0")}`;
}

function appointmentStatusIsDone(apt) {
  const s = apt.Status ?? apt.status ?? "";
  return s === "Done";
}

function formatPreferredTimeDisplay(apt) {
  const t = apt["Preferred Time"] ?? apt.preferred_time;
  if (t == null || t === "") return "TBD";
  const str = typeof t === "string" ? t : String(t);
  if (/^\d{2}:\d{2}/.test(str)) return str.slice(0, 5);
  return str;
}

const SelectDate = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => getTodayInIST());
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientProfile, setShowPatientProfile] = useState(false);

  useEffect(() => {
    fetchData();
    // Set up auto-refresh every 3 minutes
    const interval = setInterval(fetchData, 180000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.APPOINTMENTS.DATA)
      );
      const data = await response.json();

      if (data.success) {
        setAppointments(data.appointments || []);
      } else {
        console.error("Failed to fetch appointments:", data.message);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const updateAppointmentStatus = async (appointment, newStatus) => {
    const id = appointment.id;
    const timestamp = appointment.Timestamp ?? appointment.timestamp;
    const payload =
      id != null && id !== ""
        ? { id, status: newStatus }
        : { timestamp, status: newStatus };

    try {
      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.APPOINTMENTS.STATUS),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (data.success) {
        setAppointments((prev) =>
          prev.map((apt) => {
            const sameById = id != null && id !== "" && apt.id === id;
            const sameByTs =
              timestamp != null &&
              (apt.Timestamp === timestamp || apt.timestamp === timestamp);
            return sameById || sameByTs
              ? { ...apt, Status: newStatus, status: newStatus }
              : apt;
          })
        );
      } else {
        console.error("Failed to update status:", data.message);
      }
    } catch (error) {
      console.error("Error updating appointment status:", error);
    }
  };

  const deleteAppointment = async (appointment) => {
    if (!window.confirm("Are you sure you want to delete this appointment?")) {
      return;
    }

    const id = appointment.id;
    const timestamp = appointment.Timestamp ?? appointment.timestamp;
    const urlSegment =
      id != null && id !== ""
        ? String(id)
        : encodeURIComponent(String(timestamp ?? ""));

    try {
      const response = await fetch(
        buildApiUrl(`${API_ENDPOINTS.APPOINTMENTS.DELETE}/${urlSegment}`),
        {
          method: "DELETE",
        }
      );

      const data = await response.json();

      if (data.success) {
        setAppointments((prev) =>
          prev.filter((apt) => {
            if (id != null && id !== "") return apt.id !== id;
            return (
              (apt.timestamp ?? apt.Timestamp) !== timestamp
            );
          })
        );
      } else {
        console.error("Failed to delete appointment:", data.message);
      }
    } catch (error) {
      console.error("Error deleting appointment:", error);
    }
  };

  const handlePatientInfo = async (patientEmail, patientPhone) => {
    try {
      const identifier = patientEmail || patientPhone;
      if (!identifier) {
        console.error("No identifier available for patient");
        setSelectedPatient(null);
        return;
      }

      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.ADMIN.PATIENT_INFO) + "/" + identifier
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.patient) {
        setSelectedPatient(data.patient);
        setShowPatientProfile(true);
      } else {
        console.error("No patient data in response:", data);
        setSelectedPatient(null);
      }
    } catch (error) {
      console.error("Error fetching patient info:", error);
      setSelectedPatient(null);
    }
  };

  const closePatientProfile = () => {
    setShowPatientProfile(false);
    setSelectedPatient(null);
  };

  const handlePatientProfileRefresh = () => {
    fetchData();
  };

  const downloadCSV = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/appointments/files`));
      const data = await response.json();

      if (data.success) {
        alert(`CSV file location: ${data.filePaths.appointments}`);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  // Filter by preferred appointment date (CSV + PostgreSQL field names)
  const getFilteredAppointments = () => {
    return appointments.filter((appointment) => {
      const ymd = normalizePreferredDateToYmd(getPreferredDateRaw(appointment));
      return ymd != null && ymd === selectedDate;
    });
  };

  const filteredAppointments = getFilteredAppointments();

  const getSelectedDateString = () => {
    const parts = selectedDate.split("-").map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return selectedDate;
    const [y, m, d] = parts;
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading appointments for selected date...</p>
      </div>
    );
  }

  return (
    <motion.div
      className="admin-page"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="admin-header">
        <div className="header-content">
          <div className="header-left">
            <button
              className="back-btn"
              onClick={() => navigate("/admin")}
              title="Back to Dashboard"
            >
              <FaArrowLeft /> Back to Dashboard
            </button>
            <h1>📅 Select Date Appointments</h1>
          </div>
          <div className="header-right">
            <button
              className="refresh-btn"
              onClick={refreshData}
              disabled={refreshing}
            >
              <FaSync className={refreshing ? "spinning" : ""} />
              {refreshing ? " Refreshing..." : " Refresh"}
            </button>
            <button className="download-btn" onClick={downloadCSV}>
              <FaDownload /> Download CSV
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Date Selection */}
      <div className="date-controls">
        <div className="date-picker">
          <label htmlFor="date-selector">
            <FaCalendar /> Select Date:
          </label>
          <input
            type="date"
            id="date-selector"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>

        <div className="date-display">
          <h3>📅 {getSelectedDateString()}</h3>
          <p className="date-subtitle">
            View appointments for this specific date
          </p>
        </div>
      </div>

      <div className="data-section">
        <div className="section-header">
          <h2>
            Appointments for {getSelectedDateString()}
            <span className="appointment-count">
              ({filteredAppointments.length})
            </span>
          </h2>
        </div>

        {filteredAppointments.length === 0 ? (
          <div className="no-data">
            <p>No appointments scheduled for {getSelectedDateString()}.</p>
            <p className="tip">
              💡 Try selecting a different date or view all appointments.
            </p>
          </div>
        ) : (
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Patient Name</th>
                  <th>Contact Info</th>
                  <th>Service</th>
                  <th>Date & Time</th>
                  <th>Additional Info</th>
                  <th>Address & Occupation</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAppointments.map((appointment, index) => (
                  <tr key={appointment.id ?? appointment.Timestamp ?? appointment.timestamp ?? index}>
                    <td className="patient-name">
                      <strong>{appointment.Name || appointment.name}</strong>
                      <button
                        className="patient-info-btn"
                        onClick={() =>
                          handlePatientInfo(
                            appointment.Email || appointment.email,
                            appointment.Phone || appointment.phone
                          )
                        }
                        title="View Patient Profile"
                      >
                        <FaUserMd /> Patient Info
                      </button>
                    </td>
                    <td className="contact-info">
                      <div>📧 {appointment.Email || appointment.email}</div>
                      <div>📞 {appointment.Phone || appointment.phone}</div>
                    </td>
                    <td className="service">
                      <span className="service-badge">
                        {appointment["Service Required"] ||
                          appointment.service_required}
                      </span>
                    </td>
                    <td className="datetime">
                      <div className="date">
                        {formatAppointmentDate(
                          getPreferredDateRaw(appointment)
                        )}
                      </div>
                      <div className="time">
                        🕐 {formatPreferredTimeDisplay(appointment)}
                      </div>
                    </td>
                    <td className="message-cell">
                      {appointment["Additional Info"] ||
                      appointment.additional_info ? (
                        <span className="message-preview">
                          {(
                            appointment["Additional Info"] ||
                            appointment.additional_info ||
                            ""
                          ).substring(0, 50)}
                          {(appointment["Additional Info"] ||
                            appointment.additional_info ||
                            "").length > 50 && "..."}
                        </span>
                      ) : (
                        <span className="no-info">No additional info</span>
                      )}
                    </td>
                    <td className="address-occupation">
                      <div className="address">
                        {appointment["Patient Address"] ||
                        appointment.patient_address ? (
                          <span className="address-preview">
                            📍{" "}
                            {(
                              appointment["Patient Address"] ||
                              appointment.patient_address ||
                              ""
                            ).substring(0, 40)}
                            {(appointment["Patient Address"] ||
                              appointment.patient_address ||
                              "").length > 40 && "..."}
                          </span>
                        ) : (
                          <span className="no-info">No address</span>
                        )}
                      </div>
                      <div className="occupation">
                        {appointment["Patient Occupation"] ||
                        appointment.patient_occupation ? (
                          <span className="occupation-text">
                            💼{" "}
                            {appointment["Patient Occupation"] ||
                              appointment.patient_occupation}
                          </span>
                        ) : (
                          <span className="no-info">No occupation</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <button
                        className={`status-toggle ${
                          appointmentStatusIsDone(appointment)
                            ? "done"
                            : "pending"
                        }`}
                        onClick={() =>
                          updateAppointmentStatus(
                            appointment,
                            appointmentStatusIsDone(appointment) ? "" : "Done"
                          )
                        }
                        title={`Click to change status from ${
                          appointmentStatusIsDone(appointment)
                            ? "Done"
                            : "Pending"
                        } to ${
                          appointmentStatusIsDone(appointment)
                            ? "Pending"
                            : "Done"
                        }`}
                      >
                        {appointmentStatusIsDone(appointment) ? (
                          <FaToggleOn />
                        ) : (
                          <FaToggleOff />
                        )}
                        <span className="status-text">
                          {appointmentStatusIsDone(appointment) ? "✅" : "⏳"}
                        </span>
                      </button>
                    </td>
                    <td>
                      <button
                        className="delete-btn"
                        onClick={() => deleteAppointment(appointment)}
                        title="Delete this appointment"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Patient Profile Modal */}
      {showPatientProfile && (
        <PatientProfile
          patient={selectedPatient}
          onClose={closePatientProfile}
          onRefresh={handlePatientProfileRefresh}
        />
      )}
    </motion.div>
  );
};

export default SelectDate;
